import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import {
  useDeleteUserMutation,
  useGetUserQuery,
  useUpdateUserMutation,
} from '../../Api/users';

import { useGlobalState } from '../../App/globalState';
import { fromLocalPhone } from '../../Helpers/phone-formatter';
import Button from '../../Style-guide/Button';
import Input from '../../Style-guide/Input';
import ProfilePageWrapper from './style/profilePageWrapper';

import { useLogoutMutation } from '../../Api/authentication';
import useLogout from '../../Hooks/useLogout';
import useTranslate from '../../Hooks/useTranslate';
import Modal from '../../Style-guide/Modal';
import Text from '../../Style-guide/Text';
import Title from '../../Style-guide/Title';
import notify from '../../Style-guide/ToastMessage';
import validateForm from './validator';

const initialUserData = {
  firstName: '',
  lastName: '',
  currentPassword: '',
  newPassword: '',
};

const initialErrors = {
  firstName: '',
  lastName: '',
  currentPassword: '',
  newPassword: '',
};

const checkForChanges = ({ prevData, newData }) => {
  let hasChanges = false;

  Object.keys(newData).forEach((key) => {
    if (key === 'phone') {
      if (newData[key] !== fromLocalPhone(prevData.phone)) {
        hasChanges = true;
      }
    } else if (key === 'newPassword' && newData[key] !== '') {
      hasChanges = true;
    } else if (prevData[key] && prevData[key] !== newData[key]) {
      hasChanges = true;
    }
  });

  return hasChanges;
};

const Profile = () => {
  const { globalState, updateGlobalState } = useGlobalState();
  const [userData, setUserData] = useState(initialUserData);
  const [errors, setErrors] = useState(initialErrors);
  const { mutate: deleteUserRequest } = useDeleteUserMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const logout = useLogout();
  const tr = useTranslate();
  const { mutate: logoutRequest } = useLogoutMutation();
  const queryClient = useQueryClient();
  const { mutate: updateUserRequest, isSuccess: isUpdateSuccess } =
    useUpdateUserMutation();

  const {
    user: { id: userId },
  } = globalState;

  const { data, isLoading } = useGetUserQuery({
    params: { id: userId },
    enabled: !!userId,
    suspense: !!userId,
  });

  useEffect(() => {
    if (!isLoading && data) {
      const { firstName, lastName } = data;
      setUserData((prev) => ({
        ...prev,
        firstName,
        lastName,
      }));
    }
  }, [data, isLoading]);

  useEffect(() => {
    if (isUpdateSuccess) {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      updateGlobalState({
        user: {
          ...globalState.user,
          firstName: userData.firstName,
          lastName: userData.lastName,
        },
      });
    }
    // disabled for avoiding unnecessary dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateSuccess]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validateForm(userData, tr);
    setErrors(validationErrors);

    const hasChanges = checkForChanges({ prevData: data, newData: userData });
    if (!hasChanges) {
      notify.error(tr('no_changes_detected', { isError: true }));

      return;
    }
    const isFormValid = Object.values(validationErrors).every((msg) => msg === '');

    if (hasChanges && isFormValid) {
      updateUserRequest({
        data: userData,
        params: { id: userId },
      });
    }
  };

  return (
    <ProfilePageWrapper>
      <Title size='LG' className='profile-title'>
        {tr('my_profile')}
      </Title>

      <form className='profile-form' onSubmit={handleSubmit}>
        <div className='profile-container'>
          <div className='input-group'>
            <Input
              type='text'
              name='firstName'
              className='input-field'
              label={tr('first_name')}
              value={userData.firstName}
              onChange={handleChange}
              autoComplete='off'
              error={errors.firstName}
            />
            <Input
              type='text'
              name='lastName'
              className='input-field'
              label={tr('last_name')}
              value={userData.lastName}
              onChange={handleChange}
              autoComplete='off'
              error={errors.lastName}
            />
          </div>
          <div className='input-group'>
            <Input
              type='password'
              name='currentPassword'
              className='input-field'
              label={tr('current_password')}
              value={userData.currentPassword}
              onChange={handleChange}
              autoComplete='off'
              error={errors.currentPassword}
            />
            <Input
              type='password'
              name='newPassword'
              className='input-field'
              label={tr('new_password')}
              value={userData.newPassword}
              onChange={handleChange}
              autoComplete='off'
              error={errors.newPassword}
            />
          </div>
        </div>
        <Button type='submit'>{tr('save')}</Button>
        <Button
          type='button'
          className='delete-account-btn'
          $negativeAction
          onClick={() => setShowDeleteModal(true)}
        >
          <Text size='SM'>{tr('delete_account')}</Text>
        </Button>
      </form>
      <Modal
        className='delete-account-modal'
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        confirmation
        question={tr('delete_modal_text')}
        confirmText={tr('delete_account')}
        cancelText={tr('cancel')}
        onConfirm={() =>
          deleteUserRequest(
            { params: { id: userId } },
            {
              onSuccess: () => {
                logout({ redirectPath: '/', logoutRequest, globalStateKey: 'user' });
              },
            }
          )
        }
      />
    </ProfilePageWrapper>
  );
};

export default Profile;
